import React, { useState } from 'react';
import { AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import Modal from '../ui/Modal';
import { Button } from '../ui/button';
import { WorkOrderDocumentationChecklist } from '../../types';
import { getDocumentationItemClasses } from '../../utils/workOrderDocumentation';

interface DocumentationOverrideModalProps {
  isOpen: boolean;
  onClose: () => void;
  checklist: WorkOrderDocumentationChecklist | null;
  onConfirm: (reason: string) => void;
  isLoading?: boolean;
}

const DocumentationOverrideModal: React.FC<DocumentationOverrideModalProps> = ({
  isOpen,
  onClose,
  checklist,
  onConfirm,
  isLoading = false,
}) => {
  const [reason, setReason] = useState('');

  const handleClose = () => {
    setReason('');
    onClose();
  };

  const handleConfirm = () => {
    if (!reason.trim()) return;
    onConfirm(reason.trim());
  };

  if (!checklist) return null;

  const checklistItems = [
    { label: 'Photos received', complete: checklist.photosComplete },
    { label: 'Vendor invoice received', complete: checklist.vendorInvoiceComplete },
    { label: 'COI on file (primary vendor)', complete: checklist.coiComplete },
    { label: 'W-9 on file (primary vendor)', complete: checklist.w9Complete },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Documentation Incomplete"
      maxWidth="2xl"
    >
      <div className="space-y-4">
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 dark:bg-yellow-900/20 dark:border-yellow-800 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
            <div>
              <p className="font-medium text-gray-900 dark:text-gray-100">
                Required documentation is not complete
              </p>
              <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                You can mark this work order Completed with an override, but Ready to Invoice and invoicing still require all items.
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-2">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Checklist</p>
          {checklistItems.map((item) => (
            <div key={item.label} className="flex items-center gap-2 text-sm">
              {item.complete ? (
                <CheckCircle2 className="h-4 w-4 text-green-600" />
              ) : (
                <XCircle className="h-4 w-4 text-red-500" />
              )}
              <span className={getDocumentationItemClasses(item.complete)}>{item.label}</span>
            </div>
          ))}
        </div>

        {checklist.issues.length > 0 && (
          <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
            {checklist.issues.map((issue) => (
              <li key={`${issue.code}-${issue.message}`} className="flex items-start gap-2">
                <AlertTriangle
                  className={`h-4 w-4 mt-0.5 ${issue.severity === 'Blocking' ? 'text-red-500' : 'text-yellow-500'}`}
                />
                <span>{issue.message}</span>
              </li>
            ))}
          </ul>
        )}

        <div>
          <label htmlFor="documentation-override-reason" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Override Reason
          </label>
          <textarea
            id="documentation-override-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
            placeholder="Explain why you are marking this work order Completed without full documentation..."
            className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
          />
        </div>

        <div className="flex justify-end gap-3 pt-2 border-t border-gray-200 dark:border-gray-700">
          <Button type="button" variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={isLoading || !reason.trim()}
          >
            {isLoading ? 'Updating...' : 'Mark Completed Anyway'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default DocumentationOverrideModal;
